import { computed, ref, shallowRef } from 'vue'

import type { OutOfPageAdType } from '@/modules/adv/types'
import { useAdDisabledStore } from '@/store/ad-disabled-store'

import type { AdInfo } from '../types'

type AdDisabledReason = 'ad-free-mode' | 'ad-disabled'

export function useDisabledAdFiller(updateAdInfo: (patch: Partial<AdInfo>) => void) {
    const adStore = useAdDisabledStore()

    const resolveDisabledAdFiller = shallowRef<(options: { reason: AdDisabledReason }) => void>()
    const adType = ref<OutOfPageAdType>()
    const reason = computed<AdDisabledReason>(() => (adStore.isAdFreeActive ? 'ad-free-mode' : 'ad-disabled'))

    function disabledAdFillerHandler(message: { type: string; secondsLeft?: number }) {
        if (message.type === 'timer') {
            updateAdInfo({
                isVisible: true,
                secondsLeft: message.secondsLeft,
                adType: adType.value,
            })
        }

        if (message.type === 'stop') {
            updateAdInfo({ isVisible: false })
            adType.value = undefined
            resolveDisabledAdFiller.value?.({ reason: reason.value })
            resolveDisabledAdFiller.value = undefined
        }
    }

    function adDisabledHandler(adAction?: string): Promise<{ reason: AdDisabledReason }> | null {
        if (!adStore.isAdDisabled) {
            return null
        }

        if (adAction === 'showRewarded') {
            adType.value = 'rewarded'
            return new Promise((resolve) => {
                resolveDisabledAdFiller.value = resolve
            })
        }

        return Promise.resolve({ reason: reason.value })
    }

    const isDisabledAdFillerVisible = computed(() => !!resolveDisabledAdFiller.value)

    return { disabledAdFillerHandler, adDisabledHandler, isDisabledAdFillerVisible }
}
